import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import { GOOGLE_MAPS_JS_LOADER_OPTIONS } from "../../../config/googleMapsJsLoaderOptions";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const defaultCenter = { lat: 4.624335, lng: -74.063644 };

const Googlemap2 = ({ location = [], style = {}, propertyData }) => {
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(null);
  const [map, setMap] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_JS_LOADER_OPTIONS);

  const toPosition = (coords) => {
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const markers = useMemo(() => {
    const sourceList = Array.isArray(location)
      ? location
      : location && typeof location === "object"
        ? [{ ...propertyData, ...location }]
        : propertyData?.location
          ? [{ ...propertyData, ...propertyData.location }]
          : [];

    return sourceList
      .map((item, index) => {
        const position = toPosition(
          item?.coordinates ||
            item?.location?.coordinates ||
            item?.geoLocation?.coordinates,
        );
        if (!position) return null;
        return {
          position,
          item,
          key: item?._id ?? item?.id ?? `marker-${index}`,
        };
      })
      .filter(Boolean);
  }, [location, propertyData]);

  const mapCenter = markers[0]?.position || defaultCenter;
  const mapZoom = markers.length === 1 ? 14 : markers.length > 1 ? 11 : 7.7;
  const selectedMarker =
    selectedMarkerIndex !== null ? markers[selectedMarkerIndex] : null;

  const fitMapToMarkers = useCallback(
    (mapInstance) => {
      if (!mapInstance || markers.length < 2 || !window.google?.maps) return;
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(({ position }) => bounds.extend(position));
      mapInstance.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
    },
    [markers],
  );

  const onMapLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      fitMapToMarkers(mapInstance);
    },
    [fitMapToMarkers],
  );

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    setSelectedMarkerIndex(null);
    if (markers.length === 0) return;
    if (markers.length === 1) {
      map.panTo(markers[0].position);
      map.setZoom(14);
      return;
    }
    fitMapToMarkers(map);
  }, [map, markers, fitMapToMarkers]);

  const formatPrice = (value) => {
    const numberPrice = Number(value);
    if (Number.isNaN(numberPrice) || numberPrice <= 0) return "Price on request";
    return `$${numberPrice.toLocaleString()}`;
  };

  const getPriceLabel = (item) => {
    if (item?.price_display_checked === "show_actual_price" && item?.price) {
      return formatPrice(item?.price);
    }
    if (item?.price_display) return item?.price_display;
    return formatPrice(item?.price);
  };

  const bathroomsForInfo = (item) => {
    const b = item?.Bathrooms;
    if (b == null) return 0;
    if (typeof b === "string" && b.includes("+")) return b.split("+")[1] ?? b;
    return b;
  };

  if (loadError) return <div>Map is unavailable right now.</div>;
  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <GoogleMap
      mapContainerStyle={{ ...containerStyle, ...style }}
      center={mapCenter}
      zoom={mapZoom}
      options={{ disableDefaultUI: true, keyboardShortcuts: false }}
      onClick={() => setSelectedMarkerIndex(null)}
      onLoad={onMapLoad}
    >
      {markers.map(({ position, key }, index) => (
        <Marker
          key={key}
          position={position}
          onClick={() => setSelectedMarkerIndex(index)}
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={() => setSelectedMarkerIndex(null)}
        >
          <div className="w-[300px] sm:w-[340px] bg-white p-4 rounded-lg">
            <div className="flex items-start gap-3">
              {selectedMarker.item?.frontPageImg?.[0] && (
                <img
                  src={selectedMarker.item.frontPageImg[0]}
                  alt={selectedMarker.item?.name || "Property"}
                  className="w-[130px] h-[90px] object-cover rounded-lg"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  {selectedMarker.item?.status || "Property"}
                </p>
                <p className="text-sm font-bold text-[#000929] mt-0.5">
                  {getPriceLabel(selectedMarker.item)}
                </p>
                <p className="text-xs text-[#000929] mt-1 leading-5">
                  {[
                    selectedMarker.item?.street_address_number,
                    selectedMarker.item?.street_address_name,
                    selectedMarker.item?.suburb,
                  ]
                    .filter(Boolean)
                    .join(" ") || "Address not available"}
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  {selectedMarker.item?.Bedrooms ?? 0} Beds ·{" "}
                  {bathroomsForInfo(selectedMarker.item)} Baths ·{" "}
                  {selectedMarker.item?.land_size ??
                    selectedMarker.item?.house_size ??
                    selectedMarker.item?.square_area ??
                    0}{" "}
                  m²
                </p>
              </div>
            </div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default Googlemap2;
